import { memo, useState, useCallback, useLayoutEffect, useMemo, type FunctionComponent } from 'react';
// Material UI imports
import Dialog from '@mui/material/Dialog';
// Skillmore UI Components
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import AddSkillPopover from '@empathco/ui-components/src/widgets/AddSkillPopover';
// local imports
import { LookupItem } from '../models/lookupItem';
import { Skill, SkillLevel, SKILL_LEVEL_FIRST, SKILL_LEVEL_MIN } from '../models/skill';
import useCustomerSettings from '../config/customer';
import useSkillLevelConfirmGate from '../hooks/useSkillLevelConfirmGate';
import EditSkillLevel from '../v3/EditSkillLevel';
import SkillSearch, { SkillSearchProps } from './SkillSearch';

type AddSkillDialogProps = {
  readonly supervisor?: boolean;
  readonly depersonalized?: boolean;
  readonly limit?: number;
  readonly exclude?: number[];
  readonly plain?: boolean;
  readonly reducedUI?: boolean;
  readonly anchorEl?: Element | null;
  readonly onAdd: (skill: Skill, level: SkillLevel, mentoring: boolean, isTarget?: boolean) => void;
  readonly onCancel: () => void;
  readonly disabled?: boolean;
  // for Storybook only
  readonly testSkill?: Skill;
  readonly testLevel?: SkillLevel;
  readonly testConfirmed?: boolean;
};

const AddSkillDialog: FunctionComponent<AddSkillDialogProps> = ({
  supervisor = false,
  depersonalized = false,
  limit,
  exclude,
  plain = false,
  reducedUI = false,
  anchorEl,
  onAdd,
  onCancel,
  disabled = false,
  testSkill = null,
  testLevel,
  testConfirmed
}) => {
  const { HAS_MENTORING } = useCustomerSettings();
  const levelConfirmGate = useSkillLevelConfirmGate();

  const [skill, setSkill] = useState<Skill | null>(testSkill); // selected skill
  const [skl, setSkl] = useState<Skill | null>(testSkill); // detailed skill object fetched by `EditSkillLevel`
  const [level, setLevel] = useState<SkillLevel>(depersonalized ? SKILL_LEVEL_FIRST : SKILL_LEVEL_MIN);
  const [mentoring, setMentoring] = useState(false);
  const [isTarget, setIsTarget] = useState<boolean | undefined>(false);

  const isOpen = Boolean(anchorEl);

  const requireLevelConfirm = levelConfirmGate && !supervisor && !depersonalized;

  const skillSearchProps = useMemo(() => limit ? { limit } as SkillSearchProps : undefined, [limit]);

  const handleBack = useCallback(() => {
    setSkill(null);
    setSkl(null);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(
    () => skl && onAdd(skl, level, mentoring, isTarget),
    [skl, level, mentoring, isTarget, onAdd]
  );

  const handleUpdate = useCallback((sk: Skill, lvl: SkillLevel, mentor: boolean, targeting?: boolean) => {
    setSkl(sk);
    setLevel(lvl);
    if (HAS_MENTORING) setMentoring(mentor);
    setIsTarget(targeting);
  }, [HAS_MENTORING]);

  // reset on open
  useLayoutEffect(() => {
    setSkill(testSkill);
    setSkl(testSkill);
    setLevel(depersonalized ? SKILL_LEVEL_FIRST : SKILL_LEVEL_MIN);
    if (HAS_MENTORING) setMentoring(false);
    setIsTarget(false);
  }, [testSkill, isOpen, depersonalized, HAS_MENTORING]);

  return (
    <>
      <Dialog
          disableEnforceFocus
          maxWidth="lg"
          fullWidth
          scroll="body"
          open={Boolean(skill)}
          onClose={onCancel}
      >
        <CloseIconButton onClick={onCancel}/>
        {skill ? (
          <EditSkillLevel
              plain={plain}
              reducedUI={reducedUI}
              noTargeting={depersonalized || reducedUI}
              defaultLevel={depersonalized ? SKILL_LEVEL_FIRST : undefined}
              depersonalized={depersonalized}
              skill={skill}
              onUpdate={handleUpdate}
              onBack={handleBack}
              onConfirm={handleConfirm}
              disabled={disabled}
              confirmDisabled={level < SKILL_LEVEL_FIRST}
              requireLevelConfirm={requireLevelConfirm}
              testLevel={testLevel}
              testConfirmed={testConfirmed}
          />
        ) : undefined}
      </Dialog>
      <AddSkillPopover
          supervisor={supervisor}
          anchorEl={skill ? null : anchorEl}
          exclude={exclude}
          onAdd={setSkill as (skill: LookupItem) => void}
          onCancel={onCancel}
          disabled={disabled}
          SkillSearchComponentProps={skillSearchProps}
          SkillSearchComponent={SkillSearch}
      />
    </>
  );
};

export default memo(AddSkillDialog);
